// PostCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import Loading from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage, { Image } from '../component/LazyImage.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';

import { color } from '../style/variables.js';
import media from '../style/media.js';

import { getKeySystem } from '../resource/getDrmInfo.js';
import withViewportItemTracker from '../resource/withViewportItemTracker.js';

import VideoIconSource from '../../img/ic_grid_video.svg';
import MultipleIconSource from '../../img/btn_post_24.svg';

export class PostCard extends React.PureComponent {
  state = {
    isIntersecting: false,
    isServer: true,
  };
  handleIntersection = ({ isIntersecting }) => {
    this.setState({ isIntersecting });
  };
  fetchMessageDetail = () => {
    const { postId, firstAssetContentType, fetchMessageDetail } = this.props;
    const { isIntersecting } = this.state;
    if (postId && !firstAssetContentType && isIntersecting) {
      return fetchMessageDetail({
        postId,
      });
    }
    return;
  };
  fetchDrmLicenseAvailability = () => {
    const { postId, canViewMedia, fetchDrmLicenseAvailability } = this.props;
    const { isIntersecting } = this.state;
    if (!postId || canViewMedia !== null || !isIntersecting) {
      return;
    }
    return fetchDrmLicenseAvailability({
      messageId: postId,
      keySystem: getKeySystem(),
    });
  };
  componentDidMount() {
    this.fetchMessageDetail();
    this.fetchDrmLicenseAvailability();
    this.nextTick = setTimeout(() => {
      this.setState({
        isServer: false,
      });
    }, 0);
  }
  componentDidUpdate(prevProps, prevState) {
    const { postId, firstAssetContentType, canViewMedia } = this.props;
    const { isIntersecting } = this.state;
    if (
      postId !== prevProps.postId ||
      canViewMedia !== prevProps.canViewMedia ||
      isIntersecting !== prevState.isIntersecting
    ) {
      this.fetchDrmLicenseAvailability();
    }
    if (
      postId !== prevProps.postId ||
      firstAssetContentType !== prevProps.firstAssetContentType ||
      isIntersecting !== prevState.isIntersecting
    ) {
      this.fetchMessageDetail();
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }
  render() {
    const {
      postId,
      firstAssetContentType,
      jpg1x,
      jpg2x,
      webp1x,
      webp2x,
      assetIds,
      onImageLoad,
    } = this.props;
    const { isServer, isIntersecting } = this.state;
    const isMultiple = assetIds.length > 1;
    const isVideo = firstAssetContentType.startsWith('video/');
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    if (!postId) {
      return <StyledPostCard />;
    }
    return (
      <ObservablePostCardWrapper onChange={this.handleIntersection}>
        {ref => (
          <StyledPostCard ref={ref}>
            {shouldHydrate ? (
              <HydrationBoundary
                wrapper={<ImageWrapper />}
                shouldHydrate={isIntersecting}
              >
                <Picture
                  data-key="target"
                  srcSet={`${jpg1x} 1x, ${jpg2x} 2x`}
                  src={jpg1x}
                >
                  <DecryptionWrapper resourceUrl={jpg1x}>
                    <source srcSet={`${webp1x} 1x, ${webp2x} 2x`} />
                    <img
                      src={jpg1x}
                      srcSet={`${jpg1x} 1x, ${jpg2x} 2x`}
                      alt="photo"
                    />
                  </DecryptionWrapper>
                </Picture>
              </HydrationBoundary>
            ) : (
              <ImageWrapper>
                <StatefulImage>
                  <Loading data-key="error" />
                  <Loading data-key="loading" />
                  <LazyImage
                    data-key="target"
                    threshold={0.25}
                    onLoad={onImageLoad}
                  >
                    <DecryptionWrapper resourceUrl={jpg1x}>
                      <source srcSet={`${webp1x} 1x, ${webp2x} 2x`} />
                      <Image
                        srcSet={`${jpg1x} 1x, ${jpg2x} 2x`}
                        src={jpg1x}
                        alt="photo"
                      />
                    </DecryptionWrapper>
                  </LazyImage>
                </StatefulImage>
              </ImageWrapper>
            )}
            {isMultiple ? (
              <img src={MultipleIconSource} alt="multiple photos" />
            ) : isVideo ? (
              <img src={VideoIconSource} alt="video" />
            ) : null}
          </StyledPostCard>
        )}
      </ObservablePostCardWrapper>
    );
  }
}

PostCard.propTypes = {
  postId: PropTypes.string,
  firstAssetContentType: PropTypes.string,
  jpg1x: PropTypes.string,
  jpg2x: PropTypes.string,
  webp1x: PropTypes.string,
  webp2x: PropTypes.string,
  canViewMedia: PropTypes.bool,
  assetIds: PropTypes.array,
  fetchMessageDetail: PropTypes.func,
  fetchDrmLicenseAvailability: PropTypes.func,
  onImageLoad: PropTypes.func,
};

PostCard.defaultProps = {
  postId: null,
  firstAssetContentType: '',
  jpg1x: null,
  jpg2x: null,
  webp1x: null,
  webp2x: null,
  canViewMedia: null,
  assetIds: [],
  fetchMessageDetail: () => null,
  fetchDrmLicenseAvailability: () => null,
  onImageLoad: () => null,
};

const ObservablePostCardWrapper = ({ onChange, children }) => {
  const handleInViewChange = React.useCallback(
    inView => {
      onChange({
        isIntersecting: inView,
      });
    },
    [onChange]
  );

  const inViewOptions = React.useMemo(
    () => ({
      threshold: 0.1,
      triggerOnce: true,
      onChange: handleInViewChange,
    }),
    [handleInViewChange]
  );

  const { ref } = useInView(inViewOptions);
  return children(ref);
};

ObservablePostCardWrapper.propTypes = {
  onChange: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
};

const StyledPostCard = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${color.grey['300']};
  overflow: hidden;
  > img {
    position: absolute;
    z-index: 1;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
  ${media.tablet`
    > img {
      width: 24px;
      height: 24px;
    }
  `};
  ${media.mobile`
    > img {
      top: 4px;
      right: 4px;
      width: 16px;
      height: 16px;
    }
  `};
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const Picture = styled.picture`
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default withViewportItemTracker(PostCard);
